import { useMemo } from 'react';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { Trans, useTranslation } from 'react-i18next';
import {
  Session,
  useIsParticipant,
  useParticipantReferralLink,
  usePublicSessionReferral,
  useTranslateIncentiveText,
} from '@app/api';
import { SharableLink } from '@app/form';

const useStyles = createUseStyles((theme: Theme) => ({
  informationBoxContainer: {
    display: 'flex',
    position: 'relative',
    borderRadius: theme.borderRadius.large,
    padding: theme.marginBase * 4,
    paddingRight: theme.marginBase * 28,
    overflow: 'hidden',
    backgroundColor: theme.colors.blueMuted,
    [theme.media.mobile]: {
      padding: theme.marginBase * 3,
      marginTop: -theme.marginBase * 2,
    },
  },
  informationBoxContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase * 3,
  },
  illustration: {
    width: 400,
    height: 400,
    position: 'absolute',
    bottom: -200,
    right: -200,
    borderRadius: '50%',
    backgroundColor: theme.colors.blue,
    [theme.media.mobile]: {
      display: 'none',
    },
  },
  titleInformation: {
    ...theme.fonts.h5,
  },
  text: {
    ...theme.fonts.caption,
  },
  details: {
    ...theme.fonts.caption,
    whiteSpace: 'pre-line',
    padding: theme.marginBase * 2,
    borderRadius: theme.borderRadius.std,
    backgroundColor: theme.basicColors.grayBackground.fade(0.3).hexa(),
  },
  detailsIntro: {
    fontWeight: 'bold',
  },
}));

interface Props {
  participantId?: string;
  session: Session;
}

export const ReferralBlock = ({ session, participantId }: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const referralLink = useParticipantReferralLink(participantId, session);
  const { data: referral } = usePublicSessionReferral(session.id);
  const incentive = useTranslateIncentiveText(referral?.incentive);
  const isParticipant = useIsParticipant();

  const values = useMemo(
    () => ({
      incentive,
    }),
    [incentive],
  );

  if (!referral?.activated || !referralLink || !isParticipant) {
    return null;
  }

  return (
    <div className={classes.informationBoxContainer}>
      <div className={classes.informationBoxContent}>
        <h5 className={classes.titleInformation}>
          {t('publicSession.referral.title')}
        </h5>
        <p className={classes.text}>
          <Trans i18nKey="publicSession.referral.text" values={values} />
        </p>
        <SharableLink link={referralLink} />
        {!!referral?.text && (
          <div className={classes.details}>
            <div className={classes.detailsIntro}>
              {t('publicSession.referral.intro')}
            </div>
            {referral.text}
          </div>
        )}
      </div>
      <div className={classes.illustration} />
    </div>
  );
};
